import Router from 'next/router'
import PropTypes from 'prop-types'
import { v4 as uuid } from 'uuid'
import clsx from 'clsx'
import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { withI18next } from 'lib/i18n/withI18next'
import ResponsiveImage from 'components/images/responsive-image'
import StateStorage from 'lib/state-storage'
import authDuck from 'components/auth/duck'
import wishlistDuck from 'components/wishlist/duck'
import {
  SEGMENT_STORAGE_KEY_PRODUCT,
  SEGMENT_STORAGE_KEY_PROMPT_LOCATION,
} from 'lib/segment/const'
import {
  segmentSignInViewed,
  segmentTrackProductAddedToWishlist,
} from 'lib/segment'
import { appendObjToSessionObj } from 'lib/utils/common/commonUtils'
import Button from 'components/common/button'
import env from 'config/env'
import BackdropModal from 'components/portal/backdrop-modal'
import AddToBagModal from 'components/shared/add-to-bag-modal'
import Typo from 'constants/typography'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import { BUTTON_TYPES } from 'constants/button'
import styles from './styles.scss'
import ImagesSlider from './imagesSlider'

class ShopTheLookItem extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isLoading: false,
      showAddToBagModal: false,
    }

    this.sliderOptions = {
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
      },
      roundLengths: true,
      spaceBetween: 10,
    }
  }

  componentWillUnmount() {
    const { invalidateWishlist } = this.props
    invalidateWishlist()
  }

  getWishlistedItem() {
    const { product, wishlist } = this.props

    return wishlist?.products?.find(
      (item) =>
        (item.product_id || item.id_product) ===
        (product.product_id || product.id_product),
    )
  }

  getIsWishlisted() {
    return Boolean(this.getWishlistedItem())
  }

  updateWishlist = () => {
    const {
      getWishlist,
      addWishlist,
      invalidateWishlist,
      product,
      refType,
      removeWishlist,
      token,
      updateShowLogin,
    } = this.props

    if (!token) {
      const prompt_location = 'wishlist_instashop'
      segmentSignInViewed(prompt_location)
      sessionStorage.setItem(
        SEGMENT_STORAGE_KEY_PROMPT_LOCATION,
        prompt_location,
      )
      return updateShowLogin(true)
    }

    const isWishlisted = this.getIsWishlisted()

    if (!isWishlisted) {
      const productSegment = {
        ...product,
        category: {
          link_rewrite: product.category_link_rewrite,
        },
        prices: {
          discounted_price: product.discounted_price,
          original_price: product.original_price,
        },
        metadata: product.metada,
      }
      segmentTrackProductAddedToWishlist(productSegment, refType)

      invalidateWishlist()
      return addWishlist({
        product_id: product.id_product,
      }).then(() => {
        invalidateWishlist()
        getWishlist({})
      })
    }

    invalidateWishlist()
    return removeWishlist({
      wishlistId: this.getWishlistedItem()?.id_wishlist_item,
    })
  }

  getImageUrl(url) {
    if (url && typeof url === 'string' && url.charAt(0) === '/') {
      return `${env.IMG_HOST}${url.substr(1, url.length - 1)}`
    }

    return `${env.IMG_HOST}${url}`
  }

  toggleAddToBagModal = (e) => {
    if (e?.preventDefault) {
      e.preventDefault()
    }

    this.setState(({ showAddToBagModal }) => ({
      showAddToBagModal: !showAddToBagModal,
    }))
  }

  onLinkClicked = (e) => {
    const {
      product: { id_product, link_rewrite, product_id },
      refId,
      refType,
      subPosition,
    } = this.props

    if (e.ctrlKey || e.metaKey) {
      return
    }

    e.preventDefault()
    const pageUuid = uuid()
    const shop = StateStorage.getShop()
    const lang = StateStorage.getLanguage()
    const href = `/product?id=${id_product || product_id}&page_uuid=${pageUuid}`
    const as = `/${shop}/${lang}/clothes/${
      id_product || product_id
    }-${link_rewrite}.html`
    appendObjToSessionObj(SEGMENT_STORAGE_KEY_PRODUCT.stack, {
      [pageUuid]: {
        click_position: '',
        ref_id: refId,
        ref_type: refType,
        sub_position: subPosition,
      },
    })
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.id, refId)
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.position, '')
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.type, refType)
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_PRODUCT.subPos, subPosition)
    Router.push(href, as)
  }

  renderProductImages() {
    const {
      internationalization: { shop },
      product,
      t,
    } = this.props
    const {
      image_url,
      images,
      is_back_in_stock,
      quantity,
      reduction,
      reduction_formatted,
      reduction_type,
    } = product

    const isSoldOut = quantity === 0
    const hasTag = isSoldOut || is_back_in_stock
    const showReduction =
      reduction && reduction_type === 'percentage' && shop !== 5

    return (
      <div className={clsx('product-item__img', { 'with-tag': hasTag })}>
        {images && images.length ? (
          <ImagesSlider images={images} sliderOptions={this.sliderOptions} />
        ) : (
          Boolean(image_url) && (
            <ResponsiveImage
              imgRatio={1.33}
              src={this.getImageUrl(image_url)}
              phone="80vw"
              tablet="15vw"
              laptop="15vw"
              desktop="15vw"
              bigDesktop="15vw"
            />
          )
        )}
        {hasTag && (
          <div className={clsx(Typo.overline, 'product-item__label-tag')}>
            {t(is_back_in_stock && !isSoldOut ? 'Back In Stock' : 'Sold Out')}
          </div>
        )}
        {showReduction && reduction_formatted && (
          <div className={clsx(Typo.price2, 'product-item__label-reduction')}>
            {reduction_formatted}
          </div>
        )}
      </div>
    )
  }

  renderProductDetails() {
    const {
      product: {
        discounted_price,
        formatted_discounted_price,
        formatted_price,
        id_product,
        link_rewrite,
        manufacturer,
        name,
        price,
        product_name,
        quantity,
      },
      t,
    } = this.props
    const { isLoading } = this.state
    const shop = StateStorage.getShop()
    const lang = StateStorage.getLanguage()
    const as = `/${shop}/${lang}/clothes/${id_product}-${link_rewrite}.html`
    const isDiscounted = discounted_price < price

    return (
      <section className="product-item__details">
        <Icon
          alt="lookbook wishlist icon"
          className="product-item__wishlist"
          size={ICON_SIZE.medium}
          src={
            this.getIsWishlisted()
              ? ICONS.productWishlistFilled
              : ICONS.productWishlist
          }
          onClick={this.updateWishlist}
        />
        <a
          href={as}
          className={clsx(Typo.subtitle2, 'product-item__brand')}
          onClick={this.onLinkClicked}
        >
          {manufacturer?.name}
        </a>
        <a
          href={as}
          className={clsx(Typo.caption, 'product-item__name')}
          onClick={this.onLinkClicked}
        >
          {name || product_name}
        </a>
        <div className="product-item__price">
          <a
            href={as}
            className="product-item__price-wrapper"
            onClick={this.onLinkClicked}
          >
            {isDiscounted && (
              <span
                className={clsx(Typo.price1, 'product-item__price-discount')}
              >
                {formatted_discounted_price}
              </span>
            )}
            <span
              className={clsx(
                'product-item__price-full',
                isDiscounted ? `${Typo.price2} discounted` : Typo.price1,
              )}
            >
              {formatted_price}
            </span>
          </a>
        </div>
        <Button
          className="product-item__cta"
          onClick={this.toggleAddToBagModal}
          disabled={isLoading}
          type={BUTTON_TYPES.primary}
        >
          {t(quantity ? 'Add To Bag' : 'Notify Me')}
        </Button>
      </section>
    )
  }

  render() {
    const { product, setSnackBar, refId, refType } = this.props
    const { showAddToBagModal } = this.state

    if (!product) {
      return null
    }

    return (
      <div className="shop-the-look-item">
        <style jsx>{styles}</style>
        {this.renderProductImages()}
        {this.renderProductDetails()}
        <BackdropModal
          freezeBody={false}
          onBackdropClick={this.toggleAddToBagModal}
          isOpen={showAddToBagModal}
        >
          <AddToBagModal
            close={this.toggleAddToBagModal}
            enableInterstitial={false}
            onAddToBagSuccess={setSnackBar}
            onAddToBagError={setSnackBar}
            productId={product.id_product}
            ref_id={refId}
            ref_type={refType}
          />
        </BackdropModal>
      </div>
    )
  }
}

ShopTheLookItem.defaultProps = {
  product: undefined,
  refId: undefined,
  refType: undefined,
  subPosition: undefined,
  token: undefined,
  wishlist: undefined,
}

ShopTheLookItem.propTypes = {
  getWishlist: PropTypes.func.isRequired,
  addWishlist: PropTypes.func.isRequired,
  removeWishlist: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({ shop: PropTypes.number.isRequired })
    .isRequired,
  invalidateWishlist: PropTypes.func.isRequired,
  product: PropTypes.shape({
    category_link_rewrite: PropTypes.string,
    discounted_price: PropTypes.number,
    formatted_discounted_price: PropTypes.string,
    formatted_price: PropTypes.string,
    id_product: PropTypes.number,
    image_url: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({})),
    is_back_in_stock: PropTypes.bool,
    link_rewrite: PropTypes.string,
    manufacturer: PropTypes.shape({ name: PropTypes.string }),
    metada: PropTypes.shape({}),
    name: PropTypes.string,
    original_price: PropTypes.number,
    price: PropTypes.number,
    product_id: PropTypes.number,
    product_name: PropTypes.string,
    quantity: PropTypes.number,
    reduction: PropTypes.number,
    reduction_formatted: PropTypes.string,
    reduction_type: PropTypes.string,
  }),
  refId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refType: PropTypes.string,
  setSnackBar: PropTypes.func.isRequired,
  subPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  t: PropTypes.func.isRequired,
  token: PropTypes.string,
  updateShowLogin: PropTypes.func.isRequired,
  wishlist: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({})),
  }),
}

const Extend = withI18next()(ShopTheLookItem)

export default connect(
  (state) => ({
    internationalization: state.internationalization,
    isPhone: state.device.isPhone,
    token: state.auth.token,
    wishlist: state.wishlist.payload,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        getWishlist: wishlistDuck.creators.get,
        addWishlist: (option) =>
          wishlistDuck.creators.get({ ...option, type: 'ADD' }),
        removeWishlist: (option) =>
          wishlistDuck.creators.get({ ...option, type: 'DELETE' }),
        invalidateWishlist: wishlistDuck.creators.invalidate,
        updateShowLogin: authDuck.creators.updateShowLogin,
      },
      dispatch,
    ),
)(Extend)
